import { createServerFn } from "@tanstack/react-start";
import { Result } from "better-result";
import { z } from "zod/v4";
import {
  addAdminTicketMessage,
  assignAdminTicket,
  closeAdminTicket,
  deleteAdminTicket,
  deleteAdminTicketMessage,
  editAdminTicketMessage,
  getAdminTicket,
  getAdminTicketAssignees,
  getAdminTickets,
  getAdminTicketsUnreadCount,
  markAdminTicketRead,
  reopenAdminTicket,
  unassignAdminTicket,
} from "#/modules/tickets/api.tickets-admin.server";
import type { TicketsAdminSearch } from "#/modules/tickets/schema.search.admin";
import { addTicketMessageSchema } from "#/modules/tickets/tickets.functions";
import { toResult } from "#/modules/util.auth/api-errors";
import { adminMiddleware } from "#/modules/util.auth/middleware.admin";
import { toastErrorMiddleware } from "#/modules/util.errors/middleware.toast-errors";
import { apiFailure } from "#/modules/util.errors/api-failure.server";
import { toFieldErrors, translateApiError } from "#/modules/util.errors/translate";
import { client } from "#/modules/util.fetch-client/fetch-client.server";

/** GET /admin/tickets — paginated, searchable, sortable, status/type-filtered ticket overview. */
export const getAdminTicketsOverview = createServerFn({ method: "GET" })
  .middleware([adminMiddleware])
  .validator((data: TicketsAdminSearch) => data)
  .handler(async ({ data }) => {
    const result = await getAdminTickets(data);
    if (Result.isError(result)) throw result.error;

    const totalCount = result.value?.count ?? 0;
    return {
      tickets: result.value?.data ?? [],
      totalPages: Math.ceil(totalCount / data.pageSize),
      totalCount,
    };
  });

/** GET /admin/tickets/unread-count — drives the admin sidebar's "Tickets" badge. */
export const getAdminUnreadTicketCountFn = createServerFn({ method: "GET" })
  .middleware([adminMiddleware])
  .handler(async () => {
    const result = await getAdminTicketsUnreadCount();
    if (Result.isError(result)) throw result.error;
    return result.value?.count ?? 0;
  });

/** GET /admin/tickets/{id} — full ticket detail including the message thread. */
export const getAdminTicketDetail = createServerFn({ method: "GET" })
  .middleware([adminMiddleware])
  .validator(z.object({ id: z.uuid() }))
  .handler(async ({ data }) => {
    const result = await getAdminTicket(data.id);
    if (Result.isError(result)) throw result.error;
    return result.value;
  });

/** POST /admin/tickets/{id}/read — clears the unread flag; fired when the drawer opens. */
export const markAdminTicketReadFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware])
  .validator(z.object({ id: z.uuid() }))
  .handler(async ({ data }) => {
    const result = await markAdminTicketRead(data.id);
    if (Result.isError(result)) return apiFailure(result.error);
  });

/** POST /admin/tickets/{id}/messages — admin reply, reuses the same content/fileIds validator. */
export const addAdminTicketMessageFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware])
  .validator(addTicketMessageSchema)
  .handler(async ({ data }) => {
    const result = await addAdminTicketMessage(data.id, {
      content: data.content,
      fileIds: data.fileIds,
    });

    if (Result.isError(result)) {
      const fieldErrors = toFieldErrors(result.error);
      return fieldErrors ? { fieldErrors } : { formError: translateApiError(result.error) };
    }
  });

/**
 * PUT /admin/tickets/{id}/messages/{messageId} — the admin edits one of their own replies.
 * `fileIds` is the full attachment set after the edit: files left out are removed.
 */
export const editAdminTicketMessageFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware])
  .validator(addTicketMessageSchema.extend({ messageId: z.uuid() }))
  .handler(async ({ data }) => {
    const result = await editAdminTicketMessage(data.id, data.messageId, {
      content: data.content,
      fileIds: data.fileIds,
    });

    if (Result.isError(result)) {
      const fieldErrors = toFieldErrors(result.error);
      return fieldErrors ? { fieldErrors } : { formError: translateApiError(result.error) };
    }
  });

/** DELETE /admin/tickets/{id}/messages/{messageId} — the admin deletes one of their own replies. */
export const deleteAdminTicketMessageFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator(z.object({ id: z.uuid(), messageId: z.uuid() }))
  .handler(async ({ data }) => {
    const result = await deleteAdminTicketMessage(data.id, data.messageId);
    if (Result.isError(result)) return apiFailure(result.error);
  });

/** POST /admin/tickets/{id}/close */
export const closeTicketAdmin = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator(z.object({ id: z.uuid() }))
  .handler(async ({ data }) => {
    const result = await closeAdminTicket(data.id);
    if (Result.isError(result)) return apiFailure(result.error);
  });

/** POST /admin/tickets/{id}/reopen */
export const reopenTicketAdmin = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator(z.object({ id: z.uuid() }))
  .handler(async ({ data }) => {
    const result = await reopenAdminTicket(data.id);
    if (Result.isError(result)) return apiFailure(result.error);
  });

/** GET /admin/tickets/assignees — every admin the assign dropdown can pick from. */
export const getTicketAssigneesAdmin = createServerFn({ method: "GET" })
  .middleware([adminMiddleware])
  .handler(async () => {
    const result = await getAdminTicketAssignees();
    if (Result.isError(result)) throw result.error;
    return result.value?.assignees ?? [];
  });

/** POST /admin/tickets/{id}/assign — assigns the ticket to the chosen admin. */
export const assignTicketAdmin = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator(z.object({ id: z.uuid(), userId: z.string().min(1) }))
  .handler(async ({ data }) => {
    const result = await assignAdminTicket(data.id, data.userId);
    if (Result.isError(result)) return apiFailure(result.error);
  });

/** POST /admin/tickets/{id}/unassign — releases the ticket back to the unassigned pool. */
export const unassignTicketAdmin = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator(z.object({ id: z.uuid() }))
  .handler(async ({ data }) => {
    const result = await unassignAdminTicket(data.id);
    if (Result.isError(result)) return apiFailure(result.error);
  });

/** DELETE /admin/tickets/{id} — hard delete. */
export const deleteTicketAdmin = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator(z.object({ id: z.uuid() }))
  .handler(async ({ data }) => {
    const result = await deleteAdminTicket(data.id);
    if (Result.isError(result)) return apiFailure(result.error);
  });

/**
 * Multipart upload for an admin ticket reply attachment. Mirrors `uploadTicketFileFn`'s FormData
 * → `bodySerializer` pattern, posting to the admin-scoped endpoint instead.
 */
export const uploadAdminTicketFileFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware])
  .validator((data: FormData) => {
    if (!(data instanceof FormData)) {
      throw new Error("uploadAdminTicketFileFn expects FormData");
    }
    return data;
  })
  .handler(async ({ data: formData }) => {
    const file = formData.get("file");

    const result = toResult(
      await client.POST("/admin/tickets/files", {
        // The typed body is a placeholder: the serializer below sends the uploaded entry itself.
        body: { file: "" },
        bodySerializer: () => {
          const uploadFormData = new FormData();
          if (file) uploadFormData.set("file", file);
          return uploadFormData;
        },
      }),
    );

    if (Result.isError(result)) {
      return { formError: translateApiError(result.error) };
    }

    return { file: result.value.file };
  });
